<template>
  <div
    class="comment-input-section"
    :class="{ 'comment-input-section--active': !(isCommentEmpty || isSubmitDisabled) }"
  >
    <div v-if="enabledInput" class="comment-input-section__textarea__container">
      <div class="comment-input-section__textarea__inner">
        <div class="comment-input-section__textarea__dummy">{{ comment + '\n' }}</div>
        <AtomTextArea
          class="comment-input-section__textarea"
          v-model="comment"
          :id="'comment'"
          :focusOnMount="true"
          :placeholder="$tc('RaceVideoPage.highlights.comment.placeholder')"
        />
        <p v-if="hasShare && (postX || postInstagram)" class="comment-input-section__hashtag">
          {{ hashtag }}
        </p>
      </div>
    </div>

    <div class="comment-input-section__buttons">
      <span
        class="comment-input-section__placeholder"
        v-if="!enabledInput"
        v-text="$tc('RaceVideoPage.highlights.comment.placeholder')"
        @click="startInput"
      />

      <div class="comment-input-section__sns" v-if="!isPc && hasShare">
        <highlights-sns-button-parts
          :postX="postX"
          :postInstagram="postInstagram"
          :postedSns="postedSns"
          @onChangePostX="onChangePostX"
          @onChangePostInstagram="onChangePostInstagram"
        />
      </div>

      <div class="comment-input-section__buttons__comment">
        <div v-if="enabledInput" class="comment-input-section__count">
          <comment-count-parts :comment="comment" :commentLength="commentLength" />
        </div>
        <button
          v-if="enabledInput"
          class="comment-input-section__button comment-input-section__button--cancel"
          @click="handleCancelClicked"
        >
          <highlights-cancel-icon />
        </button>

        <button
          :disabled="isCommentEmpty || isSubmitDisabled"
          class="comment-input-section__button comment-input-section__button--submit"
          @click="onPostComment"
        >
          <highlights-submit-icon />
        </button>
      </div>
    </div>

    <!-- 入力キャンセル確認モーダル -->
    <confirm-modal-section
      class="comment-input-section__modal"
      v-if="enabledConfirmModal"
      :message="cancelMessage"
      :confirmButtonLabel="
        $tc('RaceVideoPage.highlights.comment.inputCancelModal.confirmButtonLabel')
      "
      :cancelButtonLabel="
        $tc('RaceVideoPage.highlights.comment.inputCancelModal.cancelButtonLabel')
      "
      @confirm="cancelInput"
      @cancel="continueInput"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, ref, PropType } from '@vue/composition-api'
import twitterText from 'twitter-text'
import AtomTextArea from '@/components/atoms/input/AtomTextArea.vue'
import ConfirmModalSection from '@/components/common/modal/ConfirmModalSection.vue'
import HighlightsCancelIcon from '@/components/RaceVideoPage/RaceVideoHighlightsPane/parts/icons/HighlightsCancelIcon.vue'
import HighlightsSubmitIcon from '@/components/RaceVideoPage/RaceVideoHighlightsPane/parts/icons/HighlightsSubmitIcon.vue'
import HighlightsSnsButtonParts from '@/components/RaceVideoPage/RaceVideoHighlightsPane/parts/HighlightsSnsButtonParts.vue'
import DeviceInfo from '@/util/DeviceInfo'
import { HighlightOperationType } from '@/components/RaceVideoPage/RaceVideoHighlightsPane.vue'
import I18n from '@/locales/I18n'
import CommentCountParts from '@/components/RaceVideoPage/RaceVideoHighlightsPane/parts/CommentCountParts.vue'
import Const from '@/util/Const'
import { HighlightPostedSnsType } from '@/store/stores/collectionModule/documents/highlight/HighlightDocument'

/**
 * レース動画再生画面 ハイライト コメント入力 セクションコンポーネント
 * 現状、以下の画面で利用するコンポーネントになっている
 * ハイライト作成
 * ハイライト編集
 * ハイライトコメント投稿
 */
export default defineComponent({
  name: 'CommentInputSection',
  components: {
    CommentCountParts,
    HighlightsSnsButtonParts,
    HighlightsSubmitIcon,
    HighlightsCancelIcon,
    ConfirmModalSection,
    AtomTextArea,
  },
  props: {
    /**
     * 対象ハイライトの入力状態
     */
    targetEnableInput: {
      type: Boolean,
      default: false,
    },
    /**
     * 対象ハイライトに対する入力コメント
     */
    targetInputHighlightComment: {
      type: String,
      default: '',
    },
    hasShare: {
      type: Boolean,
      default: false,
    },
    /**
     * Xにハイライトを投稿するかどうか
     */
    postX: {
      type: Boolean,
      default: false,
    },
    /**
     * Instagramにハイライトを投稿するかどうか
     */
    postInstagram: {
      type: Boolean,
      default: false,
    },
    /**
     * ハイライトスライドメニュータイプ
     * ハイライト作成、ハイライト編集画面から本コンポーネントを呼び出す時に渡ってくる
     */
    editType: {
      type: String as PropType<HighlightOperationType>,
    },
    /**
     * SNSに投稿したことがあるかどうか
     */
    postedSns: {
      type: Object as PropType<HighlightPostedSnsType>,
    },
  },
  setup(props, context) {
    const hashtag = process.env.VUE_APP_X_HASH_TAG
    const isPc = DeviceInfo.isForPC()

    /**
     * enabledInput
     * @param {string} val ハイライトの入力状態
     */
    const enabledInput = computed(() => props.targetEnableInput)

    const cancelMessage = computed(() => {
      if (props.editType === 'create') {
        // ハイライト作成の場合に表示
        return I18n.tc('RaceVideoPage.highlights.inputCancelModal.createCancelMessage')
      }
      if (props.editType === 'edit') {
        // ハイライト編集の場合に表示
        return I18n.tc('RaceVideoPage.highlights.inputCancelModal.editCancelMessage')
      }

      // ハイライトコメント投稿の場合に表示
      return I18n.tc('RaceVideoPage.highlights.comment.inputCancelModal.inputCancelMessage')
    })

    /**
     * comment
     * @param {string} val ハイライトに対するコメント
     */
    const comment = computed({
      get: () => props.targetInputHighlightComment,
      set: (val: string) => context.emit('onChangeHighlightComment', val),
    })

    /**
     * コメントの文字数
     */
    const commentLength = computed(() => {
      if (props.postX || props.postInstagram) {
        return twitterText.parseTweet(`${comment.value}\n${hashtag}`).weightedLength
      }
      return twitterText.parseTweet(comment.value).weightedLength
    })

    /**
     * コメントが文字数オーバーの場合、サブミットボタンをdisabledにする
     */
    const isSubmitDisabled = computed(
      () => commentLength.value > Const.MAX_HIGHLIGHT_COMMENT_LENGTH,
    )

    /**
     * コメントが空かどうか
     * スペースのみ、改行のみを空と扱う
     */
    const isCommentEmpty = computed(() => !comment.value.replace(/\s+/g, ''))

    // コメントキャンセル確認用モーダル
    const enabledConfirmModal = ref<boolean>(false)

    /**
     * コメント入力画面のキャンセルボタンが押下されたときの処理
     */
    const handleCancelClicked = () => {
      // 動画プレイヤーを非表示にする
      context.emit('hideHighlightPreviewVideoPlayer')
      enabledConfirmModal.value = true
    }

    return {
      hashtag,
      isPc,
      comment,
      commentLength,
      isSubmitDisabled,
      isCommentEmpty,
      enabledInput,
      cancelMessage,
      enabledConfirmModal,
      handleCancelClicked,
    }
  },
  methods: {
    /**
     * コメント投稿ボタンを押したことを親に伝える
     */
    async onPostComment() {
      if (this.enabledInput && !this.isCommentEmpty && !this.isSubmitDisabled) {
        // 入力状態でコメントがある場合
        this.$emit('handlerSubmit')
      } else {
        // 入力状態にする
        this.startInput()
      }
    },
    /**
     * 入力モード開始
     */
    startInput() {
      this.$emit('onToggleEnabledInput', true)
    },
    /**
     * キャンセルを押した際の処理
     */
    cancelInput() {
      this.enabledConfirmModal = false
      this.$emit('onToggleEnabledInput', false)
    },
    /**
     * 入力を続けるを押した際の処理
     */
    continueInput() {
      // スマホなら、動画プレイヤーを表示する
      this.$emit('showHighlightPreviewVideoPlayer')
      this.enabledConfirmModal = false
    },
    /**
     * Xに投稿するかどうかの値を変更する
     */
    onChangePostX(val: boolean) {
      this.$emit('onChangePostX', val)
    },
    /**
     * Instagramに投稿するかどうかの値を変更する
     */
    onChangePostInstagram(val: boolean) {
      this.$emit('onChangePostInstagram', val)
    },
  },
})
</script>

<style lang="scss" scoped>
.comment-input-section {
  display: flex;
  flex-flow: column nowrap;
  align-content: flex-start;
  justify-items: flex-start;
  width: 100%;
  background: $black;
  border: 1px solid $black90;
  border-radius: 4px;

  &__textarea {
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    width: 100%;
    height: 100%;
    min-height: 0;
    padding: 0;
    font-family: $fontSignup;
    font-weight: 500;
    font-size: 12px;
    line-height: 1.67;
    letter-spacing: 0.02em;
    color: $white;
    overflow-wrap: break-word;
    background: transparent;
    border: none;
    outline: none;
    resize: none;

    &:focus {
      background: transparent;
    }

    &::placeholder {
      font-family: $fontSignup;
      font-weight: 500;
      font-size: 12px;
      line-height: 1.67;
      letter-spacing: 0.02em;
      color: $white50;
    }

    &__container {
      padding: 12px 12px 6px;
      height: 6.51em;
      overflow-y: auto;
      font-size: 12px;
      line-height: 1.67;
    }

    &__inner {
      position: relative;
      font-size: 12px;
      line-height: 1.67;
    }

    &__dummy {
      min-height: 12 * 1.67px;
      visibility: hidden;
      overflow-wrap: break-word;
      font-family: $fontSignup;
      font-weight: 500;
      font-size: 12px;
      line-height: 1.67;
      letter-spacing: 0.02em;
      white-space: pre-wrap;
    }
  }

  &__hashtag {
    position: absolute;
    left: 0;
    bottom: -1.67em;
    font-family: $fontSignup;
    font-size: 12px;
    color: $white;
  }

  &__placeholder {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: calc(100% - 44px);
    padding: 0 12px;
    font-family: $fontSignup;
    font-weight: 500;
    font-size: 12px;
    line-height: 1.67;
    letter-spacing: 0.02em;
    color: $white50;
  }

  &__sns {
    width: 100%;
  }

  &__buttons {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    justify-content: space-between;
    width: 100%;

    &__comment {
      display: flex;
      align-items: center;
      margin-left: auto;
    }
  }

  &__button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;

    &--cancel {
      width: 24px;
      height: 24px;
      margin-left: 10px;
      color: rgba(#c2c2c2, 0.9);
    }

    &--submit {
      opacity: 0.5;
      color: $white;
    }
  }

  &--active {
    .comment-input-section__button {
      &--submit {
        opacity: 1;
        color: $green-light;
      }
    }
  }
}
</style>

<style lang="scss">
.comment-input-section {
  .highlight-item-section--landscape-primary &,
  .highlight-item-section--landscape-secondary &,
  .new-highlights-input-section--landscape-primary &,
  .new-highlights-input-section--landscape-secondary & {
    .comment-input-section__modal {
      left: auto;
      width: 230px;
    }
  }
}
</style>
